import {
  FieldElement,
  IFieldDefinition,
  LayoutElement,
  isContainerElement,
  isFieldElement,
  isImageElement,
  isLayoutTableFieldElement,
  isSectionElement,
  isTexElement,
} from "@entaconsulting/form";
import { FormControlLabel, Stack, Switch } from "@mui/material";
import EntaAccordion from "common/components/EntaAccordion";
import { PanelSections } from "developer/edit/constants";
import useDynamicField from "developer/edit/hooks/useDynamicField";
import useIsExpanded from "developer/edit/hooks/useIsExpanded";
import useIsNewestField from "developer/edit/hooks/useIsNewestField";
import React, { useCallback, useState } from "react";
import DynamicField from "./DynamicField";
import LabelField from "./commonInputFields/LabelField";
import SizeField from "./commonInputFields/SizeField";
import SourceField from "./commonInputFields/SourceField";
import TextAndTemplateField from "./commonInputFields/TextAndTemplateField";
import TitleField from "./commonInputFields/TitleField";
import RequiredField from "./schemaFields/RequiredField";

const GeneralSection = () => {
  const { isExpanded, handleChangeIsExpanded, isDefaultExpanded, hide } =
    useIsExpanded(PanelSections.General);
  const [isTemplateEnabled, setIsTemplateEnabled] = useState<boolean>(false);

  const {
    input: { value: element },
  } = useDynamicField<LayoutElement>("element");

  const {
    input: { value: fieldDef },
  } = useDynamicField<IFieldDefinition>("fieldDef");

  const handleChangeTemplateEnabled = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setIsTemplateEnabled(checked);
    },
    []
  );

  const isContainerEl = isContainerElement(element);
  const isImageEl = isImageElement(element);
  const isTextEl = isTexElement(element);
  const isTableEl = isLayoutTableFieldElement(element);
  const isFieldEl = isFieldElement(element);
  const isSectionEl = isSectionElement(element);
  const isBooleanFieldEl = isFieldEl && fieldDef.type === "boolean";

  const isNewestField = useIsNewestField((element as FieldElement)?.name);

  if (hide) return null;

  return (
    <EntaAccordion
      title="General"
      expanded={isExpanded}
      defaultExpanded={isDefaultExpanded}
      onChange={handleChangeIsExpanded}
    >
      <Stack spacing={1}>
        {isImageEl && (
          <DynamicField name="element.source">
            {(props) => (
              <SourceField
                name={props.input.name}
                value={props.input.value}
                onChange={props.input.onChange}
              />
            )}
          </DynamicField>
        )}
        {isTextEl && (
          <>
            <DynamicField
              name={`element.${isTemplateEnabled ? "template" : "label"}`}
            >
              {(props) => (
                <TextAndTemplateField
                  name={props.input.name}
                  value={props.input.value}
                  onChange={props.input.onChange}
                  label={isTemplateEnabled ? "Template" : "Text"}
                />
              )}
            </DynamicField>
            <FormControlLabel
              label="Template"
              control={
                <Switch
                  color="primary"
                  size="small"
                  checked={isTemplateEnabled}
                  onChange={handleChangeTemplateEnabled}
                />
              }
            />
          </>
        )}
        {(isFieldEl || isTableEl) && (
          <DynamicField name="element.label">
            {(props) => (
              <LabelField
                name={props.input.name}
                value={props.input.value}
                onChange={props.input.onChange}
                isNewestField={isFieldEl || isTableEl ? isNewestField : false}
              />
            )}
          </DynamicField>
        )}
        {isContainerEl && (
          <DynamicField name="element.title">
            {(props) => (
              <TitleField
                name={props.input.name}
                value={props.input.value}
                onChange={props.input.onChange}
              />
            )}
          </DynamicField>
        )}
        <Stack direction="row" spacing={1}>
          {isFieldEl && !isTableEl && !isBooleanFieldEl && (
            <DynamicField name="fieldDef.required">
              {(props) => (
                <RequiredField
                  onChange={props.input.onChange}
                  value={props.input.value}
                  name={props.input.name}
                />
              )}
            </DynamicField>
          )}
        </Stack>
        {!isSectionEl && <SizeField />}
      </Stack>
    </EntaAccordion>
  );
};

export default GeneralSection;
